import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import DefaultLayout from '../../layout/DefaultLayout';
import Loading from '../../components/Loading';
import Alert from '../../components/Alert';
import creditoService from '../../services/creditoService';
import { useGlobalState } from '../../state';
import { formatShortDate, toCurrency } from '../../utils/formaters';

type Cuota = {
  nroCuota: string | number;
  fechaVencimientoPago?: string | null;
  monto?: number;
  capital?: number;
  saldoPendiente?: number;
  interes?: number;
  fechaPago?: string | null;
};

const pillPaid = 'bg-green-50 text-green-800 px-3 py-1 rounded';
const pillPending = 'bg-red-50 text-red-800 px-3 py-1 rounded';

const ListaCuotas: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const detalleQuery = query.get('detalle');

  const [user] = useGlobalState('user');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | undefined>(undefined);
  const [cuotas, setCuotas] = useState<Cuota[] | undefined>(undefined);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      setError(undefined);

      if (user === undefined) {
        // still checking auth
        return;
      }
      if (!user) {
        navigate('/auth/signin');
        return;
      }

      try {
        // Priority:
        // 1) If id param present, try to load stored detalleCredito, fallback to API getCreditoById
        // 2) Else if ?detalle=true present, load 'detalleCredito' from localStorage
        // 3) Else load 'resultadoSimulacion' from localStorage
        if (id) {
          const stored = localStorage.getItem('detalleCredito');
          if (stored) {
            const credito = JSON.parse(stored);
            setCuotas(credito.cuotas ?? credito.Cuotas ?? []);
          } else {
            // fallback to API
            const res: any = await creditoService.getCreditoById(Number(id));
            if (res?.success) {
              const credito = res.data;
              setCuotas(credito.cuotas ?? credito.Cuotas ?? []);
              try { localStorage.setItem('detalleCredito', JSON.stringify(credito)); } catch {}
            } else {
              setError(res?.errors?.[0] ?? 'No se encontró el crédito');
            }
          }
        } else if (detalleQuery) {
          const stored = localStorage.getItem('detalleCredito');
          if (stored) {
            const credito = JSON.parse(stored);
            setCuotas(credito.cuotas ?? credito.Cuotas ?? []);
          } else {
            setError('No hay detalle de crédito disponible');
          }
        } else {
          const stored = localStorage.getItem('resultadoSimulacion');
          if (stored) {
            const resultado = JSON.parse(stored);
            setCuotas(resultado.cuotas ?? resultado.Cuotas ?? []);
          } else {
            setError('No hay resultado de simulación disponible');
          }
        }
      } catch (ex: any) {
        setError(ex?.message ?? 'Ocurrió un error al obtener las cuotas');
      } finally {
        setLoading(false);
      }
    };

    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, detalleQuery, user]);

  return (
    <DefaultLayout>
      <div className="p-2">
        <div className="max-w-6xl mx-auto bg-white dark:bg-boxdark rounded-xl p-6">
          <h1 className="text-2xl font-semibold mb-4">Tabla de Amortización</h1>

          {loading && <Loading statusText="Cargando cuotas..." />}

          {error && <Alert type="failure" message={error} />}

          {!loading && !error && (!cuotas || cuotas.length === 0) && (
            <div className="bg-white dark:bg-boxdark rounded p-6 text-center text-gray-600">
              No se encontraron cuotas
            </div>
          )}

          {!loading && cuotas && cuotas.length > 0 && (
            <div className="space-y-4">
              {cuotas.map((item, idx) => {
                const paid = !!item.fechaPago;
                return (
                  <div key={String(item.nroCuota) + '-' + idx} className="bg-gray-50 dark:bg-boxdark rounded-lg p-4 shadow-sm">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                      <div className="w-full md:w-1/2">
                        <div className="text-sm font-semibold">Cuota {item.nroCuota}</div>
                        <div className="text-xs text-gray-500 mt-1">Vencimiento: {item.fechaVencimientoPago ? formatShortDate(item.fechaVencimientoPago) : '-'}</div>
                      </div>

                      <div className="w-full md:w-1/3 text-right">
                        <div className="text-xl font-bold text-primario-celeste-800">{toCurrency(item.monto ?? 0)}</div>
                      </div>
                    </div>

                    <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-700">
                      <div>Capital: {toCurrency(item.capital ?? 0)}</div>
                      <div className="text-right md:text-left md:text-right">Saldo Pendiente: {toCurrency(item.saldoPendiente ?? 0)}</div>

                      <div>Interés: {toCurrency(item.interes ?? 0)}</div>
                      <div className="text-right">
                        {paid ? (
                          <span className={pillPaid}>Pagada: {item.fechaPago ? formatShortDate(item.fechaPago) : ''}</span>
                        ) : (
                          <span className={pillPending}>Pendiente</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            )}


            <div className="flex gap-3 justify-center mt-8">
                <button
                    onClick={() => navigate(-1)}
                    className="inline-flex items-center gap-2 bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded"
                >
                    Volver
                </button>
            </div>
        </div>
      </div>
    </DefaultLayout>
  );
};

export default ListaCuotas;